/**
 * ISO 3166-2 subdivision normalisation
 *
 * Maps full subdivision names (lowercase) to their ISO 3166-2 code for a
 * selection of countries that cover the large majority of business locations
 * worldwide.  The lookup is country-aware when a country code is supplied,
 * falling back to a global search when it is not.
 *
 * Countries covered: US, CA, AU, GB, IE, NZ, DE, FR, NL, IN, BR, MX, ZA
 */

type SubdivisionMap = Record<string, string>;

export const SUBDIVISION_LOOKUP: Record<string, SubdivisionMap> = {

  // ── United States ──────────────────────────────────────────────────────────
  US: {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN',
    'mississippi': 'MS', 'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE',
    'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
    'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK', 'oregon': 'OR',
    'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA',
    'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
    'district of columbia': 'DC', 'washington dc': 'DC', 'washington d.c.': 'DC',
    'puerto rico': 'PR', 'guam': 'GU', 'american samoa': 'AS',
    'u.s. virgin islands': 'VI', 'us virgin islands': 'VI',
    'northern mariana islands': 'MP',
  },

  // ── Canada ─────────────────────────────────────────────────────────────────
  CA: {
    'alberta': 'AB', 'british columbia': 'BC', 'manitoba': 'MB',
    'new brunswick': 'NB', 'newfoundland and labrador': 'NL',
    'newfoundland': 'NL', 'labrador': 'NL',
    'northwest territories': 'NT', 'nova scotia': 'NS', 'nunavut': 'NU',
    'ontario': 'ON', 'prince edward island': 'PE', 'pei': 'PE',
    'quebec': 'QC', 'québec': 'QC', 'saskatchewan': 'SK', 'yukon': 'YT',
  },

  // ── Australia ──────────────────────────────────────────────────────────────
  AU: {
    'new south wales': 'NSW', 'victoria': 'VIC', 'queensland': 'QLD',
    'south australia': 'SA', 'western australia': 'WA', 'tasmania': 'TAS',
    'australian capital territory': 'ACT', 'northern territory': 'NT',
  },

  // ── United Kingdom ─────────────────────────────────────────────────────────
  GB: {
    'england': 'ENG', 'scotland': 'SCT', 'wales': 'WLS',
    'northern ireland': 'NIR',
  },

  // ── Ireland ────────────────────────────────────────────────────────────────
  IE: {
    'carlow': 'CW', 'cavan': 'CN', 'clare': 'CE', 'cork': 'CO',
    'donegal': 'DL', 'dublin': 'D', 'galway': 'G', 'kerry': 'KY',
    'kildare': 'KE', 'kilkenny': 'KK', 'laois': 'LS', 'leitrim': 'LM',
    'limerick': 'LK', 'longford': 'LD', 'louth': 'LH', 'mayo': 'MO',
    'meath': 'MH', 'monaghan': 'MN', 'offaly': 'OY', 'roscommon': 'RN',
    'sligo': 'SO', 'tipperary': 'TA', 'waterford': 'WD', 'westmeath': 'WH',
    'wexford': 'WX', 'wicklow': 'WW',
  },

  // ── New Zealand ────────────────────────────────────────────────────────────
  NZ: {
    'auckland': 'AUK', 'bay of plenty': 'BOP', 'canterbury': 'CAN',
    'gisborne': 'GIS', "hawke's bay": 'HKB', 'hawkes bay': 'HKB',
    'manawatu-whanganui': 'MWT', 'manawatu whanganui': 'MWT',
    'marlborough': 'MBH', 'nelson': 'NSN', 'northland': 'NTL',
    'otago': 'OTA', 'southland': 'STL', 'taranaki': 'TKI', 'tasman': 'TAS',
    'waikato': 'WKO', 'wellington': 'WGN', 'west coast': 'WTC',
  },

  // ── Germany ────────────────────────────────────────────────────────────────
  DE: {
    'baden-württemberg': 'BW', 'baden-wurttemberg': 'BW', 'bavaria': 'BY',
    'bayern': 'BY', 'berlin': 'BE', 'brandenburg': 'BB', 'bremen': 'HB',
    'hamburg': 'HH', 'hesse': 'HE', 'hessen': 'HE',
    'lower saxony': 'NI', 'niedersachsen': 'NI',
    'mecklenburg-vorpommern': 'MV', 'mecklenburg vorpommern': 'MV',
    'north rhine-westphalia': 'NW', 'north rhine westphalia': 'NW',
    'nordrhein-westfalen': 'NW', 'rhineland-palatinate': 'RP',
    'rhineland palatinate': 'RP', 'rheinland-pfalz': 'RP',
    'saarland': 'SL', 'saxony': 'SN', 'sachsen': 'SN',
    'saxony-anhalt': 'ST', 'saxony anhalt': 'ST', 'sachsen-anhalt': 'ST',
    'schleswig-holstein': 'SH', 'schleswig holstein': 'SH',
    'thuringia': 'TH', 'thüringen': 'TH', 'thuringen': 'TH',
  },

  // ── France ─────────────────────────────────────────────────────────────────
  FR: {
    'auvergne-rhône-alpes': 'ARA', 'auvergne rhone alpes': 'ARA',
    'bourgogne-franche-comté': 'BFC', 'bourgogne franche comte': 'BFC',
    'brittany': 'BRE', 'bretagne': 'BRE',
    'centre-val de loire': 'CVL', 'centre val de loire': 'CVL',
    'corsica': 'COR', 'corse': 'COR',
    'grand est': 'GES',
    'hauts-de-france': 'HDF', 'hauts de france': 'HDF',
    'île-de-france': 'IDF', 'ile-de-france': 'IDF', 'ile de france': 'IDF',
    'normandy': 'NOR', 'normandie': 'NOR',
    'nouvelle-aquitaine': 'NAQ', 'nouvelle aquitaine': 'NAQ',
    'occitanie': 'OCC',
    'pays de la loire': 'PDL',
    "provence-alpes-côte d'azur": 'PAC', 'provence alpes cote dazur': 'PAC',
    'paca': 'PAC',
  },

  // ── Netherlands ────────────────────────────────────────────────────────────
  NL: {
    'drenthe': 'DR', 'flevoland': 'FL', 'friesland': 'FR',
    'gelderland': 'GE', 'groningen': 'GR', 'limburg': 'LI',
    'north brabant': 'NB', 'noord-brabant': 'NB', 'noord brabant': 'NB',
    'north holland': 'NH', 'noord-holland': 'NH', 'noord holland': 'NH',
    'overijssel': 'OV',
    'south holland': 'ZH', 'zuid-holland': 'ZH', 'zuid holland': 'ZH',
    'utrecht': 'UT', 'zeeland': 'ZE',
  },

  // ── India ──────────────────────────────────────────────────────────────────
  IN: {
    'andhra pradesh': 'AP', 'arunachal pradesh': 'AR', 'assam': 'AS',
    'bihar': 'BR', 'chhattisgarh': 'CG', 'goa': 'GA', 'gujarat': 'GJ',
    'haryana': 'HR', 'himachal pradesh': 'HP', 'jharkhand': 'JH',
    'karnataka': 'KA', 'kerala': 'KL', 'madhya pradesh': 'MP',
    'maharashtra': 'MH', 'manipur': 'MN', 'meghalaya': 'ML',
    'mizoram': 'MZ', 'nagaland': 'NL', 'odisha': 'OD', 'orissa': 'OD',
    'punjab': 'PB', 'rajasthan': 'RJ', 'sikkim': 'SK', 'tamil nadu': 'TN',
    'telangana': 'TG', 'tripura': 'TR', 'uttar pradesh': 'UP',
    'uttarakhand': 'UK', 'west bengal': 'WB',
    // Union Territories
    'andaman and nicobar islands': 'AN', 'chandigarh': 'CH',
    'dadra and nagar haveli and daman and diu': 'DH',
    'delhi': 'DL', 'new delhi': 'DL',
    'jammu and kashmir': 'JK', 'ladakh': 'LA', 'lakshadweep': 'LD',
    'puducherry': 'PY', 'pondicherry': 'PY',
  },

  // ── Brazil ─────────────────────────────────────────────────────────────────
  BR: {
    'acre': 'AC', 'alagoas': 'AL', 'amapá': 'AP', 'amapa': 'AP',
    'amazonas': 'AM', 'bahia': 'BA', 'ceará': 'CE', 'ceara': 'CE',
    'distrito federal': 'DF', 'espírito santo': 'ES', 'espirito santo': 'ES',
    'goiás': 'GO', 'goias': 'GO', 'maranhão': 'MA', 'maranhao': 'MA',
    'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG',
    'pará': 'PA', 'para': 'PA', 'paraíba': 'PB', 'paraiba': 'PB',
    'paraná': 'PR', 'parana': 'PR', 'pernambuco': 'PE',
    'piauí': 'PI', 'piaui': 'PI', 'rio de janeiro': 'RJ',
    'rio grande do norte': 'RN', 'rio grande do sul': 'RS',
    'rondônia': 'RO', 'rondonia': 'RO', 'roraima': 'RR',
    'santa catarina': 'SC', 'são paulo': 'SP', 'sao paulo': 'SP',
    'sergipe': 'SE', 'tocantins': 'TO',
  },

  // ── Mexico ─────────────────────────────────────────────────────────────────
  MX: {
    'aguascalientes': 'AGU', 'baja california': 'BCN',
    'baja california sur': 'BCS', 'campeche': 'CAM', 'chiapas': 'CHP',
    'chihuahua': 'CHH', 'coahuila': 'COA', 'coahuila de zaragoza': 'COA',
    'colima': 'COL', 'durango': 'DUR', 'guanajuato': 'GUA',
    'guerrero': 'GRO', 'hidalgo': 'HID', 'jalisco': 'JAL',
    'mexico city': 'CMX', 'ciudad de mexico': 'CMX', 'cdmx': 'CMX',
    'mexico state': 'MEX', 'estado de mexico': 'MEX',
    'michoacán': 'MIC', 'michoacan': 'MIC',
    'morelos': 'MOR', 'nayarit': 'NAY', 'nuevo león': 'NLE',
    'nuevo leon': 'NLE', 'oaxaca': 'OAX', 'puebla': 'PUE',
    'querétaro': 'QUE', 'queretaro': 'QUE', 'quintana roo': 'ROO',
    'san luis potosí': 'SLP', 'san luis potosi': 'SLP',
    'sinaloa': 'SIN', 'sonora': 'SON', 'tabasco': 'TAB',
    'tamaulipas': 'TAM', 'tlaxcala': 'TLA', 'veracruz': 'VER',
    'yucatán': 'YUC', 'yucatan': 'YUC', 'zacatecas': 'ZAC',
  },

  // ── South Africa ───────────────────────────────────────────────────────────
  ZA: {
    'eastern cape': 'EC', 'free state': 'FS', 'gauteng': 'GP',
    'kwazulu-natal': 'KZN', 'kwazulu natal': 'KZN',
    'limpopo': 'LP', 'mpumalanga': 'MP', 'north west': 'NW',
    'northern cape': 'NC', 'western cape': 'WC',
  },
};

/**
 * Build a global lookup (country-agnostic) from all the above maps.
 * Entries that are unique across all countries are included; ambiguous
 * ones (same name, different code) are omitted so we don't silently
 * produce incorrect codes without a country hint.
 */
const GLOBAL_LOOKUP: SubdivisionMap = (() => {
  const counts: Record<string, string[]> = {};
  for (const map of Object.values(SUBDIVISION_LOOKUP)) {
    for (const [name, code] of Object.entries(map)) {
      if (!counts[name]) counts[name] = [];
      if (!counts[name].includes(code)) counts[name].push(code);
    }
  }
  const result: SubdivisionMap = {};
  for (const [name, codes] of Object.entries(counts)) {
    if (codes.length === 1) result[name] = codes[0];
  }
  return result;
})();

/**
 * Attempt to normalise a subdivision name to its ISO 3166-2 code.
 *
 * @param value       Raw user input (e.g. "Minnesota", "New South Wales")
 * @param countryCode ISO 3166-1 alpha-2 country code, e.g. "US" (optional)
 * @returns           The ISO code (e.g. "MN") if a match is found, otherwise null
 */
export function normalizeSubdivision(value: string, countryCode?: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Already looks like a code — 1–4 uppercase letters/digits, optionally with a hyphen segment
  if (/^[A-Z0-9]{1,4}(-[A-Z0-9]{1,4})?$/.test(trimmed)) return null;

  const key = trimmed.toLowerCase();

  if (countryCode) {
    const countryMap = SUBDIVISION_LOOKUP[countryCode.toUpperCase()];
    if (countryMap?.[key]) return countryMap[key];
  }

  return GLOBAL_LOOKUP[key] ?? null;
}
